import React, { createRef, useState, useRef, useEffect } from 'react';
import qs from 'query-string';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import moment from 'moment';

import { Language, Utils } from '../../../utils';
import { user as userActions } from '../../../actions';
import { Button } from '../../../components';

import Grid from '@material-ui/core/Grid';
import makeClasses from '../styles';
import clsx from 'clsx';
import { Input } from '@material-ui/core';
import { Alert, AlertTitle } from '@material-ui/lab';
import CheckCircleOutlineOutlinedIcon from '@material-ui/icons/CheckCircleOutlineOutlined';
import CancelOutlinedIcon from '@material-ui/icons/CancelOutlined';
import PhoneAndroidOutlinedIcon from '@material-ui/icons/PhoneAndroidOutlined';
import { replace } from 'connected-react-router';

const TIMER_TIME = 60 * 5;

const StepSix = ({ traceId, affiliationStep3TraceId: affiliationStep3TraceIdProp, phone, phoneCountryId, nextStep, history }) => {
  const dispatch = useDispatch();
  const classes = makeClasses();

  const { loading, languageTexts } = useSelector(state => ({
    loading: state.user.loading,
    languageTexts: state.language.texts || {},
  }));
  const i18n = Language(languageTexts);

  const verificationCodeRefs = useRef(new Array(6).fill('').map(() => createRef()));
  const [verificationCode, setVerificationCode] = useState(new Array(6).fill(''));
  const [affiliationStep3TraceId, setAffiliationStep3TraceId] = useState(
    affiliationStep3TraceIdProp || qs.parse(history.location.search).affiliationStep3TraceId || null
  );
  const [timerStartTime, setTimerStartTime] = useState(null);
  const [intervalTime, setIntervalTime] = useState(0);
  const [intervalId, setIntervalId] = useState(null);
  const [formattedTime, setFormattedTime] = useState('00:00');
  const [stepCompleted, setStepCompleted] = useState(false);
  const [attemptLimitReachedError, setAttemptLimitReachedError] = useState(false);

  const canValidateVerificationCode = verificationCode.filter(code => /[0-9]/.test(code)).length;

  const _startTimer = () => setTimerStartTime(Date.now());

  const _stopTimer = () => {
    setTimerStartTime(null);
    if (intervalId) clearInterval(intervalId);
    setIntervalId(null);
  };

  useEffect(() => {
    setVerificationCode(new Array(6).fill(''));
    verificationCodeRefs.current[0].current.focus();
    _startTimer();
  }, []);

  useEffect(() => {
    if (!intervalId) {
      const id = setInterval(() => {
        setIntervalTime(t => t + 1);
      }, 1000);
      setIntervalId(id);
    }
  }, [intervalId]);

  useEffect(() => {
    if (timerStartTime) {
      const timer = TIMER_TIME - moment().diff(moment(timerStartTime), 's');
      if (timer < 0) {
        _stopTimer();
      } else {
        const minutes = Math.floor(timer / 60);
        const seconds = timer % 60;
        setFormattedTime(
          `${minutes < 10 ? '0' + minutes : minutes}:${seconds < 10 ? '0' + seconds : seconds}`
        );
      }
    }
  }, [intervalTime, timerStartTime]);

  const _validateVerificationCode = () => {
    const hasValidVerificationCode = verificationCode.filter(code => /[0-9]/.test(code)).length === 6;
    if (canValidateVerificationCode && hasValidVerificationCode) {
      dispatch(
        userActions.affiliationVerifyVerificationCode(
          traceId,
          affiliationStep3TraceId,
          verificationCode.join(''),
          result => {
            if (result.result === 'SUCCESSFUL') {
              _stopTimer();
              setStepCompleted(true);
            } else if (result.result === 'WRONG_ACCESS_CODE') {
              toast.error(i18n.get('Register.RegisterCodeValidationError'), { autoClose: 5000 });
            } else if (result.result === 'ATTEMPT_LIMIT_REACHED') {
              setAttemptLimitReachedError(true);
            } else if (result.result === 'EXPIRED_ACCESS_CODE') {
              _stopTimer();
              toast.error(i18n.get('Register.RegisterExpiredValidationCodeError'), { autoClose: 5000 });
            }
          }
        )
      );
    }
  };

  useEffect(() => _validateVerificationCode(), [canValidateVerificationCode]);

  const _handleVerificationCodeOnBlur = index => e => {
    if (e.target.value.length > 0 && !/[0-9]/.test(e.target.value)) {
      const newCode = verificationCode.slice();
      newCode[index] = '';
      setVerificationCode(newCode);
      verificationCodeRefs.current[index].current.focus();
    }
  };

  const _handleVerificationCodeOnChange = index => e => {
    if (e.target.value.length === 0) {
      const newCode = verificationCode.slice();
      newCode[index] = '';
      return setVerificationCode(newCode);
    }
    if (!/[0-9]/.test(e.target.value)) return;

    const newCode = verificationCode.slice();
    newCode[index] = e.target.value.substring(0, 1);
    setVerificationCode(newCode);

    if (index + 1 < 6) {
      verificationCodeRefs.current[index + 1].current.focus();
    } else if (index === 5) {
      setVerificationCode(newCode);
    }
  };

  const _handleVerificationCodeOnPaste = e => {
    const pastedText = e.clipboardData.getData('text').trim();
    if (!/[0-9]{6}/.test(pastedText)) {
      return alert(i18n.get('Register.CodeInvalidCode'));
    }
    const newCode = verificationCode.slice();
    for (let i = 0; i < 6; i += 1) {
      newCode[i] = pastedText[i];
    }
    setVerificationCode(newCode);
  };

  const _requestNewVerificationCode = () => {
    setVerificationCode(new Array(6).fill(''));
    dispatch(
      userActions.affiliationRequestPhoneVerificationCodeV2(
        traceId,
        { countryId: phoneCountryId, number: phone },
        step3TraceId => {
          if (step3TraceId === false) {
            setAttemptLimitReachedError(true);
          } else if (step3TraceId) {
            setAffiliationStep3TraceId(step3TraceId);
            const params = qs.parse(history.location.search) || {};
            params.affiliationStep3TraceId = step3TraceId;
            dispatch(replace({ search: qs.stringify(params) }));
            _startTimer();
          }
        }
      )
    );
  };

  return (
    <React.Fragment>
      <div className={classes.formContent}>
        <div className={clsx(classes.formTitleContainer, classes.formTitleContainerWithAlert)}>
          <div className={classes.formTitle}>{i18n.get('Register.StepFormTitle')}</div>
          {!attemptLimitReachedError ? (
            <div className={classes.formText}>{i18n.get('Register.Step5PhoneFormText')}</div>
          ) : null}
        </div>
        {attemptLimitReachedError ? (
          <React.Fragment>
            <Alert severity="error" icon={false} className={clsx(classes.alert, classes.alertError)}>
              <div>
                <CancelOutlinedIcon className={classes.alertIcon} />
              </div>
              <div className={classes.alertMessage}>
                <AlertTitle className={classes.alertTitle}>
                  {i18n.get('Register.AlertAttemptLimitReachedErrorTitle')}
                </AlertTitle>
                {i18n.get('Register.AlertAttemptLimitReachedErrorMessage')}
              </div>
            </Alert>
          </React.Fragment>
        ) : stepCompleted ? (
          <React.Fragment>
            <Alert severity="success" icon={false} className={clsx(classes.alert, classes.alertSuccess)}>
              <div>
                <CheckCircleOutlineOutlinedIcon className={classes.alertIcon} />
              </div>
              <div className={classes.alertMessage}>
                <AlertTitle className={classes.alertTitle}>
                  {i18n.get('Register.AlertSuccessStep3Title')}
                </AlertTitle>
                {i18n.get('Register.AlertSuccessStep3Message')}
              </div>
            </Alert>
          </React.Fragment>
        ) : (
          <React.Fragment>
            <Alert severity="warning" icon={false} className={clsx(classes.alert, classes.alertWarning)}>
              <div>
                <PhoneAndroidOutlinedIcon className={classes.alertIcon} />
              </div>
              <div className={classes.alertMessage}>
                <AlertTitle className={classes.alertTitle}>
                  {i18n.get('Register.AlertWarningStep3Title').replace('{phone}', Utils.maskPhoneNumber(phone))}
                </AlertTitle>
                {i18n.get('Register.AlertWarningStep3Message1')}
                <span className={classes.alertTimer}>{formattedTime}</span>
                <span>{i18n.get('Register.AlertWarningStep3Message2')}</span>
              </div>
            </Alert>
            <form layout="vertical" className={clsx(classes.form, classes.formVerificationCode)}>
              <Grid container spacing={2} className={classes.verificationCodeGridContainer}>
                {verificationCode.map((code, index) => (
                  <Grid key={index} item xs={2} sm={2} md={2} className={classes.codeInputContainer}>
                    <Input
                      value={code}
                      onBlur={_handleVerificationCodeOnBlur(index)}
                      onChange={_handleVerificationCodeOnChange(index)}
                      inputProps={{ className: classes.verificationCodeInput, inputMode: 'numeric' }}
                      inputRef={verificationCodeRefs.current[index]}
                      onPaste={_handleVerificationCodeOnPaste}
                      disableUnderline={true}
                    />
                  </Grid>
                ))}
                {!stepCompleted && (
                  <Grid xs={12}>
                    <Button
                      variant="outlined"
                      className={clsx(
                        classes.resendCodeButton,
                        timerStartTime ? classes.resendCodeButtonDisabled : null
                      )}
                      onClick={_requestNewVerificationCode}
                      disabled={timerStartTime}>
                      {i18n.get('Register.RequestNewVerificationCodeButton')}
                    </Button>
                  </Grid>
                )}
              </Grid>
            </form>
          </React.Fragment>
        )}
      </div>
      {!attemptLimitReachedError ? (
        <div className={clsx(classes.formSubmitContainer, classes.formSubmitContainerWithOneButton)}>
          <Button
            className={classes.formSubmitButton}
            onClick={nextStep}
            disabled={loading || !stepCompleted}>
            {i18n.get('Register.NextButton')}
          </Button>
        </div>
      ) : null}
    </React.Fragment>
  );
};

StepSix.id = 'com.Handy.Register.StepSix';
export default StepSix;
